import React, { useState, useEffect, useMemo } from 'react'
import { Search, MessageSquare, RefreshCw } from 'lucide-react'
import { adminFeedbackService } from '../services'
import './Feedback.css'

function formatWhen(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return String(iso)
  }
}

const Feedback = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const load = async () => {
    setError(null)
    setLoading(true)
    try {
      const list = await adminFeedbackService.list()
      setItems(Array.isArray(list) ? list : [])
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load feedback')
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return items
    return items.filter((row) => {
      const blob = [
        row.message,
        row.userName,
        row.fullName,
        row.email,
        row.category,
        row.userType,
        row.userId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return blob.includes(q)
    })
  }, [items, searchTerm])

  return (
    <div className="feedback-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Feedback</h1>
          <p>Messages sent from the app (Settings → Send your feedback).</p>
        </div>
        <button
          type="button"
          className="btn-refresh"
          onClick={load}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCw size={18} className={loading ? 'spin' : ''} />
        </button>
      </div>

      <div className="feedback-stats">
        <div className="feedback-stat-card">
          <MessageSquare size={22} className="feedback-stat-icon" />
          <div>
            <div className="feedback-stat-value">{items.length}</div>
            <div className="feedback-stat-label">Total submissions</div>
          </div>
        </div>
      </div>

      <div className="users-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search message, email, name, category…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="feedback-banner error" role="alert">
          {error}
        </div>
      )}

      <div className="card feedback-card">
        <div className="table-container">
          {loading ? (
            <p className="feedback-loading">Loading feedback…</p>
          ) : filtered.length === 0 ? (
            <p className="feedback-empty">
              {items.length === 0
                ? 'No feedback yet. Submissions from the mobile app will appear here.'
                : 'No rows match your search.'}
            </p>
          ) : (
            <table className="table feedback-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td className="feedback-cell-time">{formatWhen(row.createdAt)}</td>
                    <td>
                      <div className="feedback-user">
                        <div className="feedback-user-name">
                          {row.userName || row.fullName || '—'}
                        </div>
                        <div className="feedback-user-email">{row.email || '—'}</div>
                        <div className="feedback-user-meta">
                          {(row.userType || '').replace(/_/g, ' ')}
                          {row.appPlatform ? ` · ${row.appPlatform}` : ''}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`feedback-pill cat-${(row.category || 'general').toLowerCase()}`}>
                        {row.category || 'general'}
                      </span>
                      {row.source && row.source !== 'app' && (
                        <span className="feedback-pill source">{row.source}</span>
                      )}
                    </td>
                    <td className="feedback-cell-msg">
                      <div className="feedback-msg">{row.message}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default Feedback
